import { FormProvider } from "react-hook-form";
import { AppDialog } from "../../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../../common/components/button/defaultButton/DefaultButton";
import { DefaultInputField } from "../../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { DefaultFormSelectField } from "../../../../../common/components/forms/inputs/dropdown/DefaultFormSelectField";
import {
  formatSignedEurValue,
  getAmountTone,
} from "../../../../../common/utilities/currencyUtility";
import { useMergeUsersDialog } from "../hooks/useMergeUsersDialog";
import { MERGE_USERS_CONFIRMATION_WORD } from "../models/mergeUsersFormModel";
import styles from "./MergeUsersDialog.module.scss";

interface IMergeUsersDialogProps {
  /** The account being viewed: it is kept and receives everything. */
  userId?: number;
  visible: boolean;
  onHide: () => void;
}

export const MergeUsersDialog = ({
  userId,
  visible,
  onHide,
}: IMergeUsersDialogProps) => {
  const {
    form,
    candidateOptions,
    areCandidatesLoading,
    sourceUserId,
    preview,
    previewCounts,
    isPreviewLoading,
    previewErrorMessage,
    canMerge,
    isLoading,
    handleMerge,
  } = useMergeUsersDialog({ userId, visible, onHide });

  const depositBalance = preview?.depositBalance ?? 0;
  const conflicts = preview?.conflicts ?? [];
  const warnings = preview?.warnings ?? [];
  const filledFields = preview?.filledFields ?? [];

  return (
    <AppDialog
      header="Сливане на акаунти"
      visible={visible}
      onHide={onHide}
      className={styles.dialog}
      dismissableMask={false}
    >
      <FormProvider {...form}>
        <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
          <p className={styles.explanation}>
            Сливането прехвърля всичко от избрания акаунт към този: резервации,
            посещения, карти, депозит, семейство, цели, бележки, формуляри,
            отзиви и история. Празните данни на този акаунт (имейл, телефон,
            рождена дата, вход) се попълват от избрания. Избраният акаунт след
            това се изтрива.{" "}
            <span className={styles.explanation_irreversible}>
              Действието не може да бъде отменено.
            </span>
          </p>

          <DefaultFormSelectField
            name="sourceUserId"
            label="Акаунт за сливане"
            options={candidateOptions}
            isLoading={areCandidatesLoading}
            emptyMessage="Няма намерени клиенти."
            filter
            required
            width="100%"
          />

          {!!sourceUserId && isPreviewLoading && (
            <p className={styles.preview_hint}>Зареждане на прегледа...</p>
          )}

          {!!sourceUserId && previewErrorMessage && (
            <p className={styles.error} role="alert">
              {previewErrorMessage}
            </p>
          )}

          {preview && (
            <div className={styles.preview} data-testid="merge-preview">
              <p className={styles.preview_title}>
                Ще се прехвърли от {preview.sourceName}:
              </p>

              {previewCounts.length > 0 ? (
                <ul className={styles.preview_counts}>
                  {previewCounts.map((item) => (
                    <li key={item.label}>
                      <span>{item.label}</span>
                      <span className={styles.preview_count}>{item.count}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.preview_hint}>
                  Акаунтът няма записи за прехвърляне.
                </p>
              )}

              {depositBalance !== 0 && (
                <p className={styles.preview_row}>
                  Депозит:{" "}
                  <span
                    className={`${styles.preview_deposit} ${
                      styles[`preview_deposit_${getAmountTone(depositBalance)}`]
                    }`}
                  >
                    {formatSignedEurValue(depositBalance)}
                  </span>
                </p>
              )}

              {preview.familyName && (
                <p className={styles.preview_row}>
                  Семейство: <strong>{preview.familyName}</strong>
                </p>
              )}

              {filledFields.length > 0 && (
                <p className={styles.preview_row}>
                  Ще се попълнят: {filledFields.join(", ")}
                </p>
              )}
            </div>
          )}

          {preview && warnings.length > 0 && (
            <div className={styles.warnings}>
              <ul>
                {warnings.map((warning, index) => (
                  <li key={`${index}_${warning}`}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {preview && conflicts.length > 0 && (
            <div className={styles.conflicts} role="alert">
              <p className={styles.conflicts_title}>
                Сливането е блокирано, докато не бъдат решени следните
                конфликти:
              </p>
              <ul>
                {conflicts.map((conflict, index) => (
                  <li key={`${index}_${conflict}`}>{conflict}</li>
                ))}
              </ul>
            </div>
          )}

          <DefaultInputField
            name="confirmationWord"
            label={`Моля изпишете думата "${MERGE_USERS_CONFIRMATION_WORD}" в полето по-долу`}
            placeholder={MERGE_USERS_CONFIRMATION_WORD}
            width="100%"
          />

          <div className={styles.footer}>
            <DefaultButton
              label="Слей акаунтите"
              onClick={handleMerge}
              disabled={!canMerge}
              loading={isLoading}
            />
          </div>
        </form>
      </FormProvider>
    </AppDialog>
  );
};
